/* hom: hom-cart.js - House of Maryam bag (slide-in drawer and bag page).
 * Loaded on every page from snippets/hom-head.liquid (defer, after pubsub.js, global.js, hom-base.js,
 * before hom-measurements.js). Zenith's assets/cart.js still does quantities, Remove, the order note,
 * discount codes and the re-render after Add to bag; this file only adds:
 *
 *   window.HOM.cart = {
 *     open()    - opens the full bag drawer (waits for a refresh in progress). No drawer: goes to
 *                 the bag page (unless already there). Resolves true when the drawer opened.
 *     refresh() - re-renders the drawer, the bag page and the header bag count through Zenith's
 *                 Section Rendering ids (cart-drawer, cart-icon-bubble, the main-cart section,
 *                 cart-live-region-text). Resolves when done.
 *   }
 *
 *   - listens to document event 'hom:cart:changed' {detail:{key}} (sent by the measurements area
 *     after it changes a line) and refreshes; afterwards dispatches 'hom:cart:refreshed'.
 *   - a cartUpdate published by another script (quick view, wishlist, apps) also refreshes, in
 *     place of Zenith's CartItems.onCartUpdate, which would put bag-page lines into the drawer.
 *   - measurements on a line: View opens/closes the list (kept open across re-renders);
 *     Edit / Remove / Add call window.HOM.measurements.editLine / removeLine / addToLine(lineKey).
 *     html[data-hom-measurements="ready"] shows those buttons once that script is on the page.
 *   - focus: after a re-render, focus goes back to the same control of the same (or re-keyed)
 *     line, inside the drawer's focus trap; nothing moves while focus is in another dialog.
 *   - <hom-cart-recs>: bag page recommendations for the first line (sections/hom-cart-recommendations.liquid).
 */
(() => {
  const HOM = (window.HOM = window.HOM || {});
  if (HOM.cart) return;

  const root = document.documentElement;
  const expanded = new Set(); // line keys whose measurements list is open
  let lastAction = null; // { key, focus, newKey } of the last measurements button used
  let pending = null;
  let rerun = false;
  let queuedDetail = null;

  const cartUrl = () => (window.routes && window.routes.cart_url) || '/cart';
  const drawer = () => document.querySelector('cart-drawer');
  const bagPage = () => document.querySelector('.main-cart');
  const isOpen = (el) => Boolean(el) && (el.classList.contains('modal--open') || el.hasAttribute('open'));
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const escapeValue = (value) =>
    window.CSS && typeof window.CSS.escape === 'function' ? window.CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');
  const lineFor = (key) => (key ? document.querySelector(`[data-hom-line-key="${escapeValue(key)}"]`) : null);
  const visible = (el) => Boolean(el) && el.getClientRects().length > 0;

  let strings = null;
  const text = (name, fallback) => {
    if (!strings) {
      try {
        strings = JSON.parse(document.getElementById('HomCartStrings')?.textContent || '{}');
      } catch (error) {
        strings = {};
      }
    }
    return strings[name] || fallback;
  };

  /* ---------- Measurements API flag, open lists, wishlist count ---------- */

  function flagMeasurements() {
    const api = HOM.measurements;
    if (api && typeof api.editLine === 'function') {
      root.dataset.homMeasurements = 'ready';
    } else {
      delete root.dataset.homMeasurements;
    }
  }

  function setOpen(block, open) {
    if (!block) return;
    block.classList.toggle('is-open', open);
    const button = block.querySelector('[data-hom-cart-action="view"]');
    if (!button) return;
    button.setAttribute('aria-expanded', String(open));
    const label = button.querySelector('[data-hom-cart-label]');
    const next = open ? button.dataset.labelHide : button.dataset.labelView;
    if (label && next && label.textContent !== next) label.textContent = next;
  }

  function syncWishlist() {
    const api = HOM.wishlist;
    if (!api || typeof api.count !== 'function') return;
    let count = 0;
    try {
      count = Number(api.count()) || 0;
    } catch (error) {
      return;
    }
    document.querySelectorAll('[data-hom-bag-wishlist] [data-hom-wishlist-count]').forEach((el) => {
      if (el.dataset.count === String(count)) return;
      el.textContent = String(count);
      el.dataset.count = String(count);
    });
  }

  /** Runs after every re-render (ours, Zenith's, Avis's): markup arrives in its server state. */
  function afterRender() {
    flagMeasurements();
    document.querySelectorAll('[data-hom-cart-m]').forEach((block) => {
      const line = block.closest('[data-hom-line-key]');
      setOpen(block, Boolean(line) && expanded.has(line.dataset.homLineKey));
    });
    syncWishlist();
  }

  let frame = 0;
  function scheduleAfterRender() {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      afterRender();
    });
  }

  function observe() {
    if (!('MutationObserver' in window)) return;
    const observer = new MutationObserver(scheduleAfterRender);
    [drawer(), bagPage()].forEach((el) => {
      if (el) observer.observe(el, { childList: true, subtree: true });
    });
  }

  /* ---------- Focus ---------- */

  function focusLost() {
    const active = document.activeElement;
    return !active || active === document.body || active === root || !active.isConnected;
  }

  function captureFocus() {
    const active = document.activeElement;
    if (!active || focusLost()) return null;
    const line = active.closest && active.closest('[data-hom-line-key]');
    if (!line) return null;
    let focus = active.dataset.homFocus || null;
    if (!focus && active.getAttribute('name')) focus = `name:${active.getAttribute('name')}`;
    if (!focus && active.classList.contains('cart-item__name')) focus = 'name-link';
    return { key: line.dataset.homLineKey, focus };
  }

  function findInLine(key, focus) {
    const line = lineFor(key);
    if (!line) return null;
    let target = null;
    if (focus && focus.startsWith('name:')) {
      target = line.querySelector(`[name="${escapeValue(focus.slice(5))}"]`);
    } else if (focus && focus !== 'name-link') {
      target = line.querySelector(`[data-hom-focus="${escapeValue(focus)}"]`);
    }
    if (!visible(target)) target = Array.from(line.querySelectorAll('[data-hom-focus]')).find(visible) || null;
    if (!visible(target)) target = line.querySelector('.cart-item__name');
    return target;
  }

  /**
   * Put focus back after a re-render removed the focused control. Inside an open drawer the
   * Zenith focus trap is set again (its first/last elements were replaced).
   */
  function restoreFocus(keys, focus) {
    const d = drawer();
    const inDrawer = isOpen(d);
    const active = document.activeElement;

    if (!focusLost()) {
      // Focus survived (e.g. it is in the measurements dialog): only refresh the drawer trap.
      if (inDrawer && d.contains(active) && typeof trapFocus === 'function') trapFocus(d, active);
      return;
    }

    let target = null;
    for (const key of keys) {
      target = findInLine(key, focus);
      if (target) break;
    }
    const scope = inDrawer ? d : bagPage();
    if (!target && scope) {
      target =
        scope.querySelector('.cart-item__name') ||
        scope.querySelector('.cart-drawer__empty a, .main-cart__empty a') ||
        scope.querySelector('.cart-drawer__inner');
    }
    if (!target) return;
    if (inDrawer && typeof trapFocus === 'function') {
      trapFocus(d, target);
    } else {
      target.focus();
    }
  }

  function actionKeys(detail) {
    return [detail && detail.key, lastAction && lastAction.newKey, lastAction && lastAction.key].filter(Boolean);
  }

  function scheduleRestore() {
    // The measurements dialog may hand focus back to a control that was re-rendered meanwhile.
    // When it closes without a bag change (Cancel, Esc) focus is back on the line's button but a
    // dialog using Zenith's single global focus trap has removed the drawer's trap:
    // restoreFocus() then only sets the drawer trap again (when focus is inside the open drawer).
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        (pending || Promise.resolve()).then(() => {
          restoreFocus(actionKeys(null), lastAction && lastAction.focus);
        });
      })
    );
  }

  /* ---------- Refresh (Section Rendering) ---------- */

  function updateBubble(html) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !html) return;
    const source = parse(html).querySelector('.shopify-section');
    if (!source) return;
    (bubble.querySelector('.shopify-section') || bubble).innerHTML = source.innerHTML;
  }

  function updateDrawer(d, html) {
    const doc = parse(html);
    const source = doc.querySelector('.cart-drawer__inner');
    const target = d.querySelector('.cart-drawer__inner');
    if (!source || !target) return;

    const items = d.querySelector('cart-drawer-items');
    const itemsScroll = items ? items.scrollTop : 0;
    const innerScroll = target.scrollTop;

    target.innerHTML = source.innerHTML;

    const sourceRoot = doc.querySelector('cart-drawer');
    if (sourceRoot) d.classList.toggle('is-empty', sourceRoot.classList.contains('is-empty'));

    const newItems = d.querySelector('cart-drawer-items');
    if (newItems) {
      newItems.scrollTop = itemsScroll;
      if (typeof setHasScrollClass === 'function') setHasScrollClass(newItems);
    }
    target.scrollTop = innerScroll;

    // Zenith sets the note's button semantics when the drawer opens; redo it for the new markup.
    const note = d.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role') && isOpen(d) && typeof d.setSummaryAccessibility === 'function') {
      d.setSummaryAccessibility(note);
    }
  }

  // A bag page that loaded empty has no Checkout block to re-render into: reload it once it fills.
  const pageLoadedEmpty = Boolean(document.querySelector('.main-cart.is-empty'));

  function updatePage(sectionId, sections) {
    const page = bagPage();
    const cartItems = document.querySelector('cart-items');
    const empty = Boolean(parse(sections[sectionId]).querySelector('.main-cart.is-empty'));

    if (!cartItems || typeof cartItems.updateElementsAfterFetch !== 'function' || (pageLoadedEmpty && !empty)) {
      window.location.reload();
      return;
    }
    try {
      // Zenith's own list of bag page parts (lines, totals, title, empty text, live region).
      cartItems.updateElementsAfterFetch({ sections });
    } catch (error) {
      console.error('[hom-cart] bag page refresh', error);
      window.location.reload();
      return;
    }
    if (page) page.classList.toggle('is-empty', empty);
    cartItems.classList.toggle('is-empty', empty);
  }

  function announce() {
    const d = drawer();
    if (!isOpen(d)) return; // the bag page announces its new total itself (#cart-live-region-text)
    const region = d.querySelector('#CartDrawer-LiveRegionText');
    if (!region) return;
    const label = d.querySelector('.hom-bag-totals__row--total .hom-bag-totals__label, .totals__subtotal');
    const value = d.querySelector('.totals__subtotal-value > span');
    const total = label && value ? ` ${label.textContent.trim()}: ${value.textContent.trim()}.` : '';
    const message = `${text('updated', 'Bag updated')}.${total}`;
    // A short pause: the region was just re-rendered and must exist before its text changes.
    setTimeout(() => {
      region.textContent = message;
    }, 150);
  }

  async function doRefresh(detail) {
    const d = drawer();
    const page = document.getElementById('main-cart-items');
    const pageSection = page && page.dataset.id;

    const ids = ['cart-icon-bubble'];
    if (d) ids.push('cart-drawer');
    if (pageSection) ids.push(pageSection, 'cart-live-region-text');

    const before = captureFocus();
    // Section Rendering API: a plain GET with ?sections= returns {id: html}. Do NOT send
    // "Accept: application/json" here — on /cart that makes Shopify return the cart JSON instead.
    const response = await fetch(`${cartUrl()}?sections=${ids.join(',')}`, {
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const sections = await response.json();

    // A re-keyed line (its properties changed) keeps its open measurements list.
    if (detail && detail.key && lastAction && expanded.has(lastAction.key)) expanded.add(detail.key);

    updateBubble(sections['cart-icon-bubble']);
    if (d && sections['cart-drawer']) updateDrawer(d, sections['cart-drawer']);
    if (pageSection && sections[pageSection]) updatePage(pageSection, sections);

    afterRender();
    const keys = actionKeys(detail);
    if (before && before.key) keys.push(before.key);
    restoreFocus(keys, (before && before.focus) || (lastAction && lastAction.focus));
    announce();

    document.dispatchEvent(new CustomEvent('hom:cart:refreshed', { detail: Object.assign({}, detail) }));
  }

  /** Coalesces calls: one request at a time, one more afterwards if anything asked meanwhile. */
  function refresh(detail) {
    if (detail) queuedDetail = detail;
    if (pending) {
      rerun = true;
      return pending;
    }
    pending = (async () => {
      do {
        rerun = false;
        const current = queuedDetail;
        queuedDetail = null;
        try {
          await doRefresh(current);
        } catch (error) {
          console.error('[hom-cart] refresh', error);
        }
      } while (rerun);
    })().finally(() => {
      pending = null;
    });
    return pending;
  }

  function open(opener) {
    return (pending || Promise.resolve()).then(() => {
      const d = drawer();
      if (d && typeof d.open === 'function') {
        if (isOpen(d)) return true;
        let trigger = opener instanceof HTMLElement ? opener : document.activeElement;
        if (!trigger || trigger === document.body || !trigger.isConnected || d.contains(trigger)) {
          trigger = document.getElementById('cart-icon-bubble');
        }
        d.open(trigger);
        return true;
      }
      if (!document.getElementById('main-cart-items')) window.location.assign(cartUrl());
      return false;
    });
  }

  HOM.cart = { open, refresh };

  /* ---------- Zenith's cartUpdate re-render ---------- */

  // Zenith's CartItems.onCartUpdate (inherited by <cart-drawer-items>) runs when any script
  // publishes PUB_SUB_EVENTS.cartUpdate. It renders the standalone "main-cart-items" section
  // into itself: in the drawer that swaps in bag-page lines and a form with another id (the
  // drawer's Checkout button, form="CartDrawer-Form", then submits nothing), and on the bag page
  // the totals and title stay stale. Route it through refresh() instead, once per publish.
  // Add to bag (source "product-form") is left alone when the drawer is the element Zenith's
  // ProductForm renders into. With the "Just added" pop-up on the page (hom_cart_open_drawer off
  // and enable_cart_notification on) ProductForm renders the pop-up instead, so the drawer
  // refreshes here too (otherwise it keeps stale lines, or stays blank if the bag was empty).
  let lastUpdateSource = null;
  let updateTimer = 0;
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      lastUpdateSource = (event && event.source) || null;
    });
  }
  function onZenithCartUpdate() {
    if (updateTimer) return;
    // After every subscriber of this publish has run, so the source is known.
    updateTimer = setTimeout(() => {
      updateTimer = 0;
      const source = lastUpdateSource;
      if (source === 'product-form' && drawer() && !document.querySelector('cart-notification')) return;
      refresh({ source });
    });
  }
  if (window.customElements) {
    customElements.whenDefined('cart-items').then(() => {
      const CartItemsElement = customElements.get('cart-items');
      const proto = CartItemsElement && CartItemsElement.prototype;
      if (!proto || proto.homCartUpdate) return;
      proto.homCartUpdate = true;
      proto.onCartUpdate = onZenithCartUpdate;
    });
  }

  /* ---------- Measurements buttons (event delegation; lines are re-rendered often) ---------- */

  const methods = { edit: 'editLine', remove: 'removeLine', add: 'addToLine' };

  function runMeasurements(method, key, button) {
    const api = HOM.measurements;
    if (!api || typeof api[method] !== 'function') return;
    lastAction = { key, focus: button.dataset.homFocus || null, newKey: null };
    let result;
    try {
      result = api[method](key);
    } catch (error) {
      console.error('[hom-cart] measurements', error);
      return;
    }
    Promise.resolve(result)
      .then((value) => {
        if (value && typeof value === 'object' && value.key) lastAction.newKey = value.key;
      })
      .catch((error) => console.error('[hom-cart] measurements', error))
      .finally(scheduleRestore);
  }

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-hom-cart-action]') : null;
    if (!button) return;
    const line = button.closest('[data-hom-line-key]');
    const key = line ? line.dataset.homLineKey : null;
    const action = button.dataset.homCartAction;

    if (action === 'view') {
      const block = button.closest('[data-hom-cart-m]');
      const openList = button.getAttribute('aria-expanded') !== 'true';
      setOpen(block, openList);
      if (key) {
        if (openList) expanded.add(key);
        else expanded.delete(key);
      }
      return;
    }

    const method = methods[action];
    if (!method || !key) return;
    event.preventDefault();
    runMeasurements(method, key, button);
  });

  document.addEventListener('hom:cart:changed', (event) => {
    refresh((event && event.detail) || {});
  });

  document.addEventListener('hom:wishlist:change', syncWishlist);

  /* ---------- Bag page recommendations ---------- */

  class HomCartRecs extends HTMLElement {
    connectedCallback() {
      HomCartRecs.instances.add(this);
      if (this.observer || this.loaded) return;
      if (!this.dataset.productId) {
        this.hide();
        return;
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.stopObserving();
            this.load();
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        this.observer.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      HomCartRecs.instances.delete(this);
      this.stopObserving();
    }

    stopObserving() {
      if (!this.observer) return;
      this.observer.disconnect();
      this.observer = null;
    }

    get wrapper() {
      return this.closest('.hom-cart-recs-section') || this;
    }

    hide() {
      this.replaceChildren();
      this.wrapper.hidden = true;
    }

    async load() {
      const id = this.dataset.productId;
      if (!id || !this.dataset.url) {
        this.hide();
        return;
      }
      this.loaded = true;
      const token = (this.token = (this.token || 0) + 1);
      try {
        const response = await fetch(`${this.dataset.url}${encodeURIComponent(id)}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        if (token !== this.token) return;
        const source = parse(html).querySelector('hom-cart-recs');
        if (!source || !source.querySelector('[data-hom-carousel-track] > li')) {
          this.hide();
          return;
        }
        this.replaceChildren(...Array.from(source.childNodes, (node) => document.importNode(node, true)));
        this.wrapper.hidden = false;
      } catch (error) {
        console.error('[hom-cart] recommendations', error);
        this.hide();
      }
    }

    /** After a bag change: follow the first line, or hide for an empty bag. */
    async sync() {
      try {
        const response = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        const first = cart.items && cart.items[0];
        const id = first ? String(first.product_id) : '';
        if (!id) {
          this.dataset.productId = '';
          this.stopObserving();
          this.hide();
          return;
        }
        if (id === this.dataset.productId) return;
        this.dataset.productId = id;
        if (!this.observer) this.load(); // not reached yet: the observer loads the new product
      } catch (error) {
        console.error('[hom-cart] recommendations', error);
      }
    }
  }
  HomCartRecs.instances = new Set();

  let recsTimer = 0;
  const syncRecs = () => {
    if (!HomCartRecs.instances.size) return;
    clearTimeout(recsTimer);
    recsTimer = setTimeout(() => HomCartRecs.instances.forEach((el) => el.sync()), 400);
  };
  document.addEventListener('hom:cart:refreshed', syncRecs);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, syncRecs);
  }

  if (!customElements.get('hom-cart-recs')) customElements.define('hom-cart-recs', HomCartRecs);

  /* ---------- Start ---------- */

  const init = () => {
    afterRender();
    observe();
  };
  // Deferred scripts run before DOMContentLoaded, so hom-measurements.js (loaded after this file)
  // has defined HOM.measurements by then.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  window.addEventListener('load', flagMeasurements, { once: true });
})();
